import type { CrudFormProps } from '@@partner:types:app'
import type { GQL_ID } from '@@partner:types:graphql'
import type { AssemblerFormState } from './AssemblerFormState'

import React, { useCallback, useEffect, useMemo } from 'react'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import { useFetchSingleAssembler } from '@@partner:graphql/query/employees/singleAssembler.gql'
import { useMutateSaveAssembler } from '../../../graphql/mutation/employees/saveAssembler.gql'
import {
  makeAssemblerFormState,
  mapApiDataToState,
  mapStateToApiVariable
} from './AssemblerFormState'
import {
  notifyOk,
  notifyError,
  makeGraphQLErrorNotification
} from '@@partner:helpers/notification'

import { Form, SelectPicker } from 'rsuite'
import CustomerPhone from '@components/order/form/CustomerPhone'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import { SaveButton } from '@components/common/form/Buttons'

import { AssemblerFormValidationModel } from '../validation/AssemblerFormValidationModel'

import { ASSEMBLER_ROLES, EMPLOYEE_STATUS_LABELS } from '@@partner:config/employee'
import { useFetchShops } from '../../../graphql/query/shop/shops.gql'

export interface AssemblerFormProps extends CrudFormProps {
  id?: GQL_ID
}

export default function AssemblerForm ({ id, onSaved }: AssemblerFormProps) {
  const {
    data,
    loading: loadingAssembler,
    error
  } = useFetchSingleAssembler({ id })

  const { data: shopsData, loading: loadingShopsData } = useFetchShops()

  const shops = useMemo(
    () =>
      shopsData?.shops?.map(
        (item) => ({ label: item.name, value: Number(item.id) })
      ) ?? [],
    [ shopsData ]
  )

  const {
    formState,
    setFormState,
    setFormRef,
    handleFormChange,
    handleFormError
  } = useValidatableForm<AssemblerFormState>(makeAssemblerFormState)

  const [ saveAssembler, { loading: loadingSaveAssembler } ] =
    useMutateSaveAssembler()

  useEffect(() => {
    if (!loadingAssembler && data) {
      setFormState((state) =>
        mergeLeft(state, mapApiDataToState(data?.assembler))
      )
    }
  }, [ data, loadingAssembler, setFormState ])

  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        saveAssembler({
          variables: {
            assemblerInfo: mapStateToApiVariable(formState),
            upsert: !id
          }
        })
          .then(({ data }) => {
            notifyOk('Данные сборщика сохранёны')
            setFormState((state) =>
              mergeLeftOnly([ 'id' ], state, data?.assembler)
            )

            onSaved?.(data?.assembler.id)
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ saveAssembler, formState, id, setFormState, onSaved ]
  )

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        error={error}
        title="Ошибка загрузки курьера"
      />
    )
  }

  return (
    <div className="AssemblersForm">
      <Form
        model={AssemblerFormValidationModel}
        formValue={formState}
        onChange={handleFormChange}
        onSubmit={handleFormSubmit}
        onCheck={handleFormError}
        ref={setFormRef}
        onError={handleFormError}
        layout="horizontal"
      >
        <h3 className="u-form-heading">Сборщик</h3>

        <Form.Group>
          <Form.ControlLabel>Фамилия</Form.ControlLabel>
          <Form.Control name="firstName" placeholder="Фамилия" />
        </Form.Group>
        <Form.Group>
          <Form.ControlLabel>Имя</Form.ControlLabel>
          <Form.Control name="lastName" placeholder="Имя" />
        </Form.Group>
        <Form.Group>
          <CustomerPhone name="phone" label="Телефон" />
        </Form.Group>
        <Form.Group>
          <Form.ControlLabel>Статус</Form.ControlLabel>
          <Form.Control
            name="status"
            accepter={SelectPicker}
            data={EMPLOYEE_STATUS_LABELS}
            searchable={false}
            cleanable={false}
          />
        </Form.Group>

        <Form.Group>
          <Form.ControlLabel>Магазин</Form.ControlLabel>
          <Form.Control
            name="shopId"
            accepter={SelectPicker}
            loading={loadingShopsData}
            data={shops}
            searchable={false}
            cleanable={false}
          />
        </Form.Group>
        <Form.Group>
          <Form.ControlLabel>Роль</Form.ControlLabel>
          <Form.Control
            name="role"
            accepter={SelectPicker}
            data={ASSEMBLER_ROLES}
            searchable={false}
            cleanable={false}
          />
        </Form.Group>

        <SaveButton
          loading={loadingAssembler || loadingSaveAssembler}
          disabled={loadingAssembler || loadingSaveAssembler}
        >
          Сохранить
        </SaveButton>
      </Form>
    </div>
  )
}
